'use client';

import { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Check, AlertCircle, Loader2, Bell, Sparkles } from 'lucide-react';

interface ComingSoonEmailProps {
  /** Title above the input */
  title?: string;
  /** Description text */
  description?: string;
  /** Placeholder text for the email input */
  placeholder?: string;
  /** Button text */
  buttonText?: string;
  /** Success message after signup */
  successMessage?: string;
  /** Primary accent color */
  accentColor?: string;
  /** Feature or context for the notification */
  feature?: string;
  /** Optional class name */
  className?: string;
  /** Custom submit handler - if not provided, stores in localStorage */
  onSubmit?: (email: string, feature: string) => Promise<{ success: boolean; message?: string }>;
}

function ComingSoonEmailComponent({
  title = 'Get Notified',
  description = "Be the first to know when we launch. We'll send you one email — no spam, ever.",
  placeholder = 'Enter your email',
  buttonText = 'Notify Me',
  successMessage = "You're on the list! We'll let you know when we launch.",
  accentColor = '#00D4FF',
  feature = 'marketplace',
  className = '',
  onSubmit,
}: ComingSoonEmailProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === 'loading') return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      if (onSubmit) {
        const result = await onSubmit(email, feature);
        if (result.success) {
          setStatus('success');
          setMessage(result.message || successMessage);
        } else {
          setStatus('error');
          setMessage(result.message || 'Something went wrong. Please try again.');
        }
      } else {
        // Default: store in localStorage
        const key = 'lykbl_coming_soon_signups';
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        if (existing.some((e: { email: string }) => e.email === email)) {
          setStatus('error');
          setMessage("You're already on the list!");
          return;
        }
        existing.push({ email, feature, timestamp: new Date().toISOString() });
        localStorage.setItem(key, JSON.stringify(existing));
        setStatus('success');
        setMessage(successMessage);
      }
    } catch (err) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className={`w-full max-w-md mx-auto ${className}`}>
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="text-center py-6"
          >
            <motion.div
              className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${accentColor}30, ${accentColor}10)`,
                border: `2px solid ${accentColor}`,
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            >
              <Check className="w-8 h-8" style={{ color: accentColor }} />
            </motion.div>
            <motion.p
              className="text-lg font-medium text-white mb-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              You're on the list!
            </motion.p>
            <motion.p
              className="text-sm text-white/60"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {message}
            </motion.p>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Header */}
            <div className="text-center mb-6">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-3"
                style={{
                  background: `${accentColor}15`,
                  color: accentColor,
                  border: `1px solid ${accentColor}30`,
                }}
              >
                <Bell className="w-3 h-3" />
                {title}
              </div>
              <p className="text-sm text-white/60">{description}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="relative">
              <div
                className="relative rounded-2xl overflow-hidden transition-all duration-300"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isFocused ? accentColor + '50' : 'rgba(255,255,255,0.1)'}`,
                  boxShadow: isFocused ? `0 0 20px -5px ${accentColor}30` : 'none',
                }}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === 'error') setStatus('idle');
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={placeholder}
                  className="w-full bg-transparent px-5 py-4 pr-32 text-white placeholder-white/40 focus:outline-hidden"
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || !email}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                  style={{
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                    color: '#0a0a0f',
                    boxShadow: `0 4px 16px -4px ${accentColor}50`,
                  }}
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <span>{buttonText}</span>
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              {/* Error message */}
              <AnimatePresence>
                {status === 'error' && message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -10, height: 0 }}
                    className="mt-3 flex items-center gap-2 text-sm text-red-400"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{message}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            {/* Trust signal */}
            <motion.div
              className="mt-4 flex items-center justify-center gap-2 text-xs text-white/40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Sparkles className="w-3 h-3" />
              <span>Join 250+ creators waiting for launch</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const ComingSoonEmail = memo(ComingSoonEmailComponent);
